import { Controller } from "@hotwired/stimulus"
import { positionAnchoredPopup } from "../utils/dropdown_utils"

// Generic anchor + panel popup: prefers below the anchor; flips above when the panel does
// not fit below (see positionAnchoredPopup). Uses the same
// .dropdown-menu / .dropdown-closed classes as nav menus for open/close animation.
//
// Markup: wrapper (this.element) position:relative; panel absolute left-0; no top/bottom in CSS.
// Optional trigger target: set aria-expanded when opening/closing.
// Optional: data-action on panel "click->anchored-popup#stopPropagation" when the panel sits
// inside a <summary> so clicks do not toggle parent <details>.
//
// Click: data-action="click->anchored-popup#toggle" on the trigger (info icons).
// Hover: data-action="mouseenter->anchored-popup#open mouseleave->anchored-popup#close"
// on the wrapper (e.g. map filter prompt).
// Hover with a click fallback: use hoverOpen/hoverClose instead of open/close. They no-op on
// touch devices, where an emulated mouseenter would otherwise fight the trigger's click.
//
// Position is applied while the panel still has .dropdown-closed so the first painted open
// frame is already placed. Inline top/bottom are not cleared on close (avoids a jump during
// the fade); the next open overwrites them.

const CLOSED_CLASS = "dropdown-closed"
const CLOSE_OTHERS = "anchored-popup:closeOthers"

export default class extends Controller {
  static targets = ["menu", "trigger"]

  connect() {
    this.clickOutsideHandler = this.clickOutside.bind(this)
    this.closeOthersHandler = this.closeOthers.bind(this)
    this.beforeCacheHandler = this.closeMenu.bind(this)
    document.addEventListener(CLOSE_OTHERS, this.closeOthersHandler)
    document.addEventListener("turbo:before-cache", this.beforeCacheHandler)
    this.setExpanded(false)
  }

  disconnect() {
    document.removeEventListener("click", this.clickOutsideHandler)
    document.removeEventListener(CLOSE_OTHERS, this.closeOthersHandler)
    document.removeEventListener("turbo:before-cache", this.beforeCacheHandler)
    this.setExpanded(false)
  }

  closeMenu() {
    if (this.hasMenuTarget && !this.menuTarget.classList.contains(CLOSED_CLASS)) {
      this.shutPanel()
    }
  }

  closeOthers(event) {
    if (event.detail?.openingElement !== this.element && this.hasMenuTarget && !this.menuTarget.classList.contains(CLOSED_CLASS)) {
      this.shutPanel()
    }
  }

  open() {
    this.openPanel()
  }

  close() {
    this.shutPanel()
  }

  hoverOpen() {
    if (!this.hoverCapablePointer()) return

    this.openPanel()
  }

  hoverClose() {
    if (!this.hoverCapablePointer()) return

    this.shutPanel()
  }

  hoverCapablePointer() {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches
  }

  toggle(event) {
    event.stopPropagation()
    if (!this.hasMenuTarget) return

    if (this.menuTarget.classList.contains(CLOSED_CLASS)) {
      this.openPanel()
    } else {
      this.shutPanel()
    }
  }

  openPanel() {
    if (!this.hasMenuTarget) return

    const menu = this.menuTarget
    if (!menu.classList.contains(CLOSED_CLASS)) return

    document.dispatchEvent(new CustomEvent(CLOSE_OTHERS, { detail: { openingElement: this.element } }))
    document.addEventListener("click", this.clickOutsideHandler)
    positionAnchoredPopup(menu, this.element)
    menu.classList.remove(CLOSED_CLASS)
    this.setExpanded(true)
    requestAnimationFrame(() => {
      positionAnchoredPopup(menu, this.element)
    })
  }

  clickOutside(event) {
    if (this.hasMenuTarget && !this.element.contains(event.target)) {
      this.shutPanel()
    }
  }

  shutPanel() {
    if (!this.hasMenuTarget) return
    if (this.menuTarget.classList.contains(CLOSED_CLASS)) return
    this.menuTarget.classList.add(CLOSED_CLASS)
    document.removeEventListener("click", this.clickOutsideHandler)
    this.setExpanded(false)
  }

  setExpanded(open) {
    if (!this.hasTriggerTarget) return
    this.triggerTarget.setAttribute("aria-expanded", open ? "true" : "false")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }
}
